/**
 * `<ChannelCTA>` — silent-by-default chat-channel CTA for entity detail pages
 * (Story / CVE / Briefing item). Walks the entity's loaded ontology tags in
 * priority order (`security-domain → industry → region`) and renders a single
 * "Join the {slug} channel →" link for the first dimension that maps to an
 * enabled Discourse chat channel.
 *
 * Server contract (Task 16): `GET /api/community/channel-map/:dim/:slug`
 * returns 404 for missing OR disabled rows; 200 only when a row exists with
 * `enabled = true`. Frontend therefore only checks `r.ok` before reading.
 *
 * Plan: 2 - CyberFind/CyberFindIntelHub/docs/superpowers/plans/2026-04-26-intelhub-community-discuss-integration-plan.md
 *       (Task 16 — lines 1755-1786)
 */

import { useEffect, useState } from 'react';

export interface ChannelCTAOntologyTags {
  'security-domain'?: string[];
  industry?: string[];
  region?: string[];
}

export interface ChannelCTAProps {
  /**
   * Tags already loaded on the entity's detail page (no extra fetch). Missing
   * dimensions default to empty — the component silently renders nothing if
   * no priority dimension yields an enabled channel.
   */
  ontologyTags: ChannelCTAOntologyTags;
}

const PRIORITY: Array<keyof ChannelCTAOntologyTags> = [
  'security-domain',
  'industry',
  'region',
];

interface ResolvedChannel {
  slug: string;
  href: string;
}

export function ChannelCTA({ ontologyTags }: ChannelCTAProps) {
  const [channel, setChannel] = useState<ResolvedChannel | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const dim of PRIORITY) {
        const slugs = ontologyTags[dim] ?? [];
        for (const slug of slugs) {
          try {
            const r = await fetch(
              `/api/community/channel-map/${encodeURIComponent(dim)}/${encodeURIComponent(slug)}`,
              { credentials: 'include' },
            );
            if (cancelled) return;
            if (!r.ok) continue;
            const j = (await r.json()) as {
              enabled?: boolean;
              discourseChannelSlug?: string;
              url?: string;
            };
            if (j?.enabled && j.discourseChannelSlug && j.url) {
              if (!cancelled) {
                setChannel({ slug: j.discourseChannelSlug, href: j.url });
              }
              return;
            }
          } catch {
            // Network blip — try next slug. Silent component, never noisy.
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ontologyTags]);

  if (!channel) return null;
  return (
    <a
      className="cta-channel inline-flex items-center gap-1 text-sm text-primary hover:underline"
      href={channel.href}
      target="_blank"
      rel="noreferrer noopener"
    >
      # Join the {channel.slug} channel →
    </a>
  );
}
